import { useTranslation } from 'react-i18next';
import { DialogWrapper } from '../common/dialogWrapper';
import { Box } from '@mui/material';
import { FsButton } from '../button';
import { useEffect, useState } from 'react';
import { FinancialStatementDateHelper } from '../../../helpers/financialStatementDateHelper';
import { FormikProvider, useFormik } from 'formik';
import { InputWrapper } from '../formUtilities/useInput';
import { UserService } from '../../../services/userService';
import { Toaster } from '../common/toaster';
import { CURRENCY } from '../../../config/appConfig';

export const FinancialStatementDialog = ({
	financialStatementRatios = null,
	state = false,
	onClose = () => { },
	onSubmitForm = (values) => { },
}) => {
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const userService = new UserService();
	const years = FinancialStatementDateHelper();
	const createYearPair = () => [
		{ year: years.year1Key, value: '' },
		{ year: years.year2, value: '' },
	];

	const initialValues = {
		financials: {
			revenue: createYearPair(),
			netProfit: createYearPair(),
			flatRateBankLoans: createYearPair(),
			accountsReceivable: createYearPair(),
			inventory: createYearPair(),
			cash: createYearPair(),
			fixedAssets: createYearPair(),
			accountsPayable: createYearPair(),
			bankLiabilities: createYearPair(),
			equity: createYearPair(),
		},
	};

	const form = useFormik({
		initialValues: initialValues,
		onSubmit: () => {
			onSubmit();
		},
	});
	const { t } = useTranslation();

	const getNumber = (value) => parseFloat(value || 0);

	const getComputedValues = (index) => {
		const revenue = getNumber(form.values.financials.revenue[index].value);
		const netProfit = getNumber(form.values.financials.netProfit[index].value);
		const flatRate = getNumber(form.values.financials.flatRateBankLoans[index].value);
		const accountsReceivable = getNumber(
			form.values.financials.accountsReceivable[index].value
		);
		const inventory = getNumber(form.values.financials.inventory[index].value);
		const cash = getNumber(form.values.financials.cash[index].value);
		const fixedAssets = getNumber(form.values.financials.fixedAssets[index].value);
		const accountsPayable = getNumber(
			form.values.financials.accountsPayable[index].value
		);
		const bankLiabilities = getNumber(
			form.values.financials.bankLiabilities[index].value
		);
		const equity = getNumber(form.values.financials.equity[index].value);

		const totalAssets = accountsReceivable + inventory + cash + fixedAssets;
		const interestExpense = bankLiabilities * (flatRate / 100);
		const operatingCashflow = netProfit;
		const totalOutstandingDebt = bankLiabilities;

		return {
			revenue,
			netProfit,
			flatRate,
			accountsReceivable,
			inventory,
			cash,
			fixedAssets,
			accountsPayable,
			bankLiabilities,
			equity,
			totalAssets,
			interestExpense,
			operatingCashflow,
			totalOutstandingDebt,
		};
	};

	const formatRatio = (value) => {
		if (!Number.isFinite(value)) {
			return 'NA';
		}
		return Number(value).toFixed(2);
	};

	const ratioRows = [
		{
			label: 'Revenue Growth (%)',
			getValue: (current, previous) =>
				previous.revenue
					? ((current.revenue - previous.revenue) / Math.abs(previous.revenue)) * 100
					: NaN,
		},
		{
			label: 'Net Profit Margin (%)',
			getValue: (current) => (current.revenue ? (current.netProfit / current.revenue) * 100 : NaN),
		},
		{
			label: 'Return On Equity (%)',
			getValue: (current) => (current.equity ? (current.netProfit / current.equity) * 100 : NaN),
		},
		{
			label: 'Return On Assets (%)',
			getValue: (current) =>
				current.totalAssets ? (current.netProfit / current.totalAssets) * 100 : NaN,
		},
		{
			label: 'Total outstanding debt',
			getValue: (current) => current.totalOutstandingDebt,
		},
		{
			label: 'Inventory Days',
			getValue: (current) => (current.revenue ? (current.inventory / current.revenue) * 365 : NaN),
		},
		{
			label: 'Debtor Days',
			getValue: (current) =>
				current.revenue ? (current.accountsReceivable / current.revenue) * 365 : NaN,
		},
		{
			label: 'Creditor Days',
			getValue: (current) =>
				current.revenue ? (current.accountsPayable / current.revenue) * 365 : NaN,
		},
		{
			label: 'Cash Conversion Cycle',
			getValue: (current) =>
				current.revenue
					? ((current.inventory + current.accountsReceivable - current.accountsPayable) /
							current.revenue) *
					  365
					: NaN,
		},
		{
			label: 'Debt Ratio (%)',
			getValue: (current) =>
				current.totalAssets ? (current.bankLiabilities / current.totalAssets) * 100 : NaN,
		},
		{
			label: 'Interest Cover Ratio (%)',
			getValue: (current) =>
				current.interestExpense ? (current.netProfit / current.interestExpense) * 100 : NaN,
		},
		{
			label: 'Debt Service Coverage Ratio (%)',
			getValue: (current) =>
				current.interestExpense
					? ((current.operatingCashflow + current.interestExpense) /
							current.interestExpense) *
					  100
					: NaN,
		},
		{
			label: 'Debt To Equity (%)',
			getValue: (current) =>
				current.equity ? (current.bankLiabilities / current.equity) * 100 : NaN,
		},
		{
			label: 'Current Ratio',
			getValue: (current) =>
				current.accountsPayable
					? (current.cash + current.accountsReceivable + current.inventory) /
					  current.accountsPayable
					: NaN,
		},
		{
			label: 'Quick Ratio',
			getValue: (current) =>
				current.accountsPayable
					? (current.cash + current.accountsReceivable) / current.accountsPayable
					: NaN,
		},
		{
			label: 'Operating Cashflow',
			getValue: (current) => current.operatingCashflow,
		},
	];

	const getLegacyValue = (data, path, index) => {
		const sections = path.split('.');
		let curr = data;
		for (const section of sections) {
			curr = curr?.[section];
		}
		return curr?.[index]?.value ?? '';
	};

	const mapIncomingValues = (data) => {
		if (!data) {
			return initialValues;
		}
		if (data.financials?.revenue?.length) {
			return data;
		}
		return {
			financials: {
				revenue: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'incomeStatements.totalRevenue', index),
				})),
				netProfit: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'incomeStatements.netProfit', index),
				})),
				flatRateBankLoans: createYearPair(),
				accountsReceivable: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'assets.accountReceivables', index),
				})),
				inventory: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'assets.inventory', index),
				})),
				cash: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'assets.cashOnHandAtBank', index),
				})),
				fixedAssets: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'assets.ppAndE', index),
				})),
				accountsPayable: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'liability.accountPayable', index),
				})),
				bankLiabilities: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'liability.borrowings', index),
				})),
				equity: createYearPair().map((item, index) => ({
					...item,
					value: getLegacyValue(data, 'liability.totalOwnersEquity', index),
				})),
			},
		};
	};

	useEffect(() => {
		form.setValues(mapIncomingValues(financialStatementRatios));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [financialStatementRatios]);

	const requiredFields = [
		'revenue',
		'netProfit',
		'flatRateBankLoans',
		'accountsReceivable',
		'inventory',
		'cash',
		'fixedAssets',
		'accountsPayable',
		'bankLiabilities',
		'equity',
	];

	const validateRequiredFields = () => {
		let hasErrors = false;
		requiredFields.forEach((field) => {
			[0, 1].forEach((index) => {
				const value = form.values.financials[field]?.[index]?.value;
				if (value === '' || value === null || value === undefined) {
					form.setFieldError(`financials.${field}[${index}].value`, 'required');
					hasErrors = true;
				}
			});
		});
		return !hasErrors;
	};

	const buildLegacyPayload = () => {
		const toYearPair = (field) =>
			form.values.financials[field].map((item) => ({
				year: item.year,
				value: item.value,
			}));

		const getValue = (field, index) => getNumber(form.values.financials[field][index].value);
		const totalAssets = [0, 1].map((index) =>
			getValue('accountsReceivable', index) +
			getValue('inventory', index) +
			getValue('cash', index) +
			getValue('fixedAssets', index)
		);
		const totalLiabilityAndEquity = [0, 1].map(
			(index) => getValue('accountsPayable', index) + getValue('bankLiabilities', index) + getValue('equity', index)
		);

		return {
			assets: {
				cashOnHandAtBank: toYearPair('cash'),
				accountReceivables: toYearPair('accountsReceivable'),
				inventory: toYearPair('inventory'),
				other: createYearPair(),
				ppAndE: toYearPair('fixedAssets'),
				totalAssets: createYearPair().map((item, index) => ({
					...item,
					value: totalAssets[index],
				})),
			},
			liability: {
				accountPayable: toYearPair('accountsPayable'),
				borrowings: toYearPair('bankLiabilities'),
				other: createYearPair(),
				totalLiabilities: createYearPair().map((item, index) => ({
					...item,
					value: getValue('accountsPayable', index) + getValue('bankLiabilities', index),
				})),
				paidInCapital: toYearPair('equity'),
				retainedEarnings: createYearPair(),
				currentYearNetProfit: createYearPair(),
				ownersEquityAccount: createYearPair(),
				totalOwnersEquity: toYearPair('equity'),
				totalLiabilityAndEquity: createYearPair().map((item, index) => ({
					...item,
					value: totalLiabilityAndEquity[index],
				})),
			},
			incomeStatements: {
				totalRevenue: toYearPair('revenue'),
				totalExpenses: createYearPair().map((item, index) => ({
					...item,
					value: getValue('revenue', index) - getValue('netProfit', index),
				})),
				netProfit: toYearPair('netProfit'),
			},
			financials: form.values.financials,
		};
	};

	const onSubmit = () => {
		if (!validateRequiredFields()) {
			Toaster({
				msg: 'Please fill all highlighted fields.',
			});
			return;
		}

		const payload = buildLegacyPayload();
		setLoadingSubmit(true);
		userService
			.addFinancialStatementRatios(payload)
			.then(() => {
				setLoadingSubmit(false);
				onSubmitForm(payload);
			})
			.catch(() => {
				setLoadingSubmit(false);
			});
	};

	const inputRows = [
		{ label: 'Revenue', field: 'revenue' },
		{ label: 'Net profit', field: 'netProfit' },
		{ label: 'Flat Rate% via bank loans (Avg)', field: 'flatRateBankLoans' },
		{ label: 'Accounts receivable', field: 'accountsReceivable' },
		{ label: 'Inventory', field: 'inventory' },
		{ label: 'Cash', field: 'cash' },
		{ label: 'Fixed Assets', field: 'fixedAssets' },
		{ label: 'Accounts payable', field: 'accountsPayable' },
		{ label: 'Bank liabilities', field: 'bankLiabilities' },
		{ label: 'Equity', field: 'equity' },
	];

	const renderInputRow = ({ label, field }) => {
		return (
			<Box key={field} className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 items-center">
				<Box className="text-[13px]">{label}</Box>
				{form.values.financials[field]?.map((item, index) => (
					<InputWrapper
						key={`${field}-${index}`}
						hideErrorMessage
						type="number"
						name={`financials.${field}[${index}].value`}
					/>
				))}
			</Box>
		);
	};

	const currentYearValues = getComputedValues(0);
	const previousYearValues = getComputedValues(1);

	const renderRatioRow = (row) => {
		return (
			<Box key={row.label} className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 items-center">
				<Box className="text-[13px]">{row.label}</Box>
				<Box className="text-[13px] px-3 py-1 border rounded bg-[#f7f7f7]">
					{formatRatio(row.getValue(currentYearValues, previousYearValues))}
				</Box>
				<Box className="text-[13px] px-3 py-1 border rounded bg-[#f7f7f7]">
					{formatRatio(row.getValue(previousYearValues, currentYearValues))}
				</Box>
			</Box>
		);
	};

	return (
		<DialogWrapper
			maxWidth="md"
			state={state}
			onClose={() => {
				onClose(false);
			}}
			subTitle={t('financialStatementText')}
			title={t('financialStatement')}
			content={
				<Box data-testid="financialStatementPopup">
					<Box className="flex flex-col border rounded-lg p-3 max-h-[68vh] overflow-y-auto">
						<FormikProvider value={form}>
							<Box className="text-[12px] font-semibold mb-3">
								INSTRUCTIONS: RM SHOULD ONLY FILL HIGHLIGHTED FIELDS ON FINANCIALS & AR+AP SHEET
							</Box>
							<Box className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 items-center border-b pb-2 mb-3">
								<Box className="font-semibold">Year / Item ({CURRENCY})</Box>
								<Box className="font-semibold">{`YTD ${years.year1}`}</Box>
								<Box className="font-semibold">{`31/12/${years.year2}`}</Box>
							</Box>
							<Box className="flex flex-col gap-2">
								{inputRows.map(renderInputRow)}
							</Box>
							<Box className="font-semibold mt-4 mb-2">Ratios</Box>
							<Box className="flex flex-col gap-2">{ratioRows.map(renderRatioRow)}</Box>
						</FormikProvider>
					</Box>
				</Box>
			}
			actions={
				<Box className="flex flex-col">
					<Box className="flex justify-center items-center">
						<FsButton
							variant="outline"
							className="!mx-2 md:!w-[150px]"
							onClick={() => {
								onClose(false);
							}}
						>{t('close')}</FsButton>
						<FsButton
							loading={loadingSubmit}
							className="md:!w-[150px]"
							onClick={() => {
								form.submitForm();
							}}
						>{t('submit')}</FsButton>
					</Box>
				</Box>
			}></DialogWrapper>
	);
};
